import { FC, useCallback, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { BackButton } from 'components/UI/BackButton';
import { Button } from 'components/UI/Button';
import { Heading } from 'components/UI/Heading';
import { InputField } from 'components/UI/InputField';
import { Loader } from 'components/UI/Loader';
import { SuccessState } from 'components/UI/SuccessState';
import { useTranslations } from 'hooks/useTranslations';
import { useBrand } from 'hooks/useBrand';
import { useAppSelector } from 'hooks/redux';
import { ResendActivationData } from 'models/diam';
import { localizationSelector } from 'store/localizationState';
import { useResendActivationMutation } from 'store/diamApi';
import { mailRegex } from 'utils/constants';
import { Description, InputFieldContainer } from './styled';

export const ResendActivationPage: FC = () => {
  const t = useTranslations();
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const { brand } = useBrand();
  const { languageCode } = useAppSelector(localizationSelector);
  const [resendActivation, resendActivationResult] =
    useResendActivationMutation();

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        username: z
          .string()
          .min(1, t('validation.required'))
          .regex(mailRegex, t('validation.email_address'))
      }),
    [t]
  );

  type FormData = z.infer<typeof schema>;

  // useForm
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Submit
  const onSubmit: SubmitHandler<FormData> = useCallback(
    ({ username }) => {
      const resendActivationData: ResendActivationData = {
        email: username,
        meta: {
          language: languageCode,
          ...(brand && { brand })
        }
      };

      resendActivation(resendActivationData);
      setEmailAddress(username);
    },
    [brand, languageCode, resendActivation]
  );

  if (resendActivationResult.isLoading) {
    return <Loader />;
  }

  if (resendActivationResult.isSuccess) {
    return (
      <SuccessState
        heading={t('resend_activation.title_success')}
        details={
          <>
            <Description $center>
              {t('resend_activation.description_resend_activation_email')}
            </Description>
            <Description $center>
              <strong>{emailAddress}</strong>
            </Description>
          </>
        }
        iconType="email"
      />
    );
  }

  return (
    <>
      <BackButton />
      <Heading>{t('resend_activation.title')}</Heading>
      <Description>{t('resend_activation.description')}</Description>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFieldContainer>
          <InputField
            id="username"
            label={t('resend_activation.label_email_address')}
            required
            placeholder={t('resend_activation.placeholder_email_address')}
            error={errors.username}
            register={register('username')}
          />
        </InputFieldContainer>
        <Button type="submit">
          {t('resend_activation.send_activation_link')}
        </Button>
      </form>
    </>
  );
};
